"use client";
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Tabs, Flex, Button, Modal, Typography, App } from "antd";
import {
  IconSquareRoundedNumber1Filled,
  IconSquareRoundedNumber2Filled,
  IconSquareRoundedNumber3Filled,
  IconSquareRoundedNumber4Filled,
  IconArrowNarrowLeft,
  IconArrowNarrowRight,
  IconCheck,
  IconSquareRoundedNumber5Filled,
  IconSquareRoundedNumber6Filled,
} from "@/lib/icons";
import { useRouter, useSearchParams, usePathname } from "next/navigation";

import {
  getAvailableSteps,
  getNextStep,
  getPrevStep,
  deriveMultiDateCondition,
  debugFlow,
} from "./stepFlow";
import VenueStep from "./VenueStep";
import DateTimeStep from "./DateTimeStep";
import SlotStep from "./SlotStep";
import SeasonPassStep from "./SeasonPassStep";
import TicketStep from "./ticketStep";
import Review from "./Review";
import UserWrapper from "../Wrappers/UserWrapper";
import SeatingLayout from "../Seating-layout/SeatingLayout";
import { usercontext } from "@/lib/context";
import { DeleteOutlined } from "@ant-design/icons";
import ZoneLayout from "../zone-layout/ZoneLayout";
import { SEAT_INTERACTION_MODE } from "../zone-layout/seatInteractionConfig";
import { clearStoredSessionId, createSeatSession, deleteSession } from "@/lib/seatSession";
import { ENDPOINTS } from "@/constants/endpoint";
import { DEV_AUTH_HEADERS } from "@/lib/devHeaders";
import { logger } from "@/lib/logger";

const stepNumberIcons = [
  <IconSquareRoundedNumber1Filled key="1" size={20} />,
  <IconSquareRoundedNumber2Filled key="2" size={20} />,
  <IconSquareRoundedNumber3Filled key="3" size={20} />,
  <IconSquareRoundedNumber4Filled key="4" size={20} />,
  <IconSquareRoundedNumber5Filled key="5" size={20} />,
  <IconSquareRoundedNumber6Filled key="6" size={20} />,
];

const getStepIcon = (stepKey, index) => {
  if (stepKey === "review") return <IconCheck size={20} />;
  return stepNumberIcons[index] || stepNumberIcons[0];
};
const ComponentToRender = ({
  render,
  data,
  activeStep,
  setActiveStep,
  selectedSeats,
  setSelectedSeats,
  isGroup,
  needsVenueSelection,
  setOrderDetails,
  orderDetails,
  setCustomModal,
  loading: parentLoading,
  createOrder,
  is_required,
  approvalModal,
  setApprovalModal,
  dates,
  setDates,
  tickets: ticketsProp,
  setTickets,
  setLoading: setParentLoading,
  haveConfiguredSeats,
  is_sitting,
  is_zone,
  isFinished,
  onNoZones,
  alreadyRegistered,
  isTicketTransferable,
  enrollmentOrderId,
  slotShowId,
}) => {
  const { message } = App.useApp();
  const interactionMode =
    orderDetails?.interactionMode || SEAT_INTERACTION_MODE;
  const router = useRouter();
  const pathname = usePathname();
  const { user, setIsOpen, loading, setSpecialFn } = useContext(usercontext);
  const [goToSeatselection, setGoToSeatSelection] = useState(false);
  // Slot shows price tickets per day, so their tickets live in state too.
  const tickets = isGroup
    ? ticketsProp
    : slotShowId
      ? (ticketsProp ?? data?.tickets)
      : data?.tickets;
  const searchParams = useSearchParams();
  const is_application = searchParams.get("is_application");

  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);
  const prevStepRef = useRef(activeStep);

  const { Text } = Typography;
  const calculateTotals = (ticket) => {
    if (!ticket || !Array.isArray(ticket.seats)) {
      return { totalSeats: 0, totalPrice: 0 };
    }

    const totalSeats = ticket.seats.length;

    const totalPrice = ticket.seats.reduce(
      (sum, seat) => sum + (seat?.price || 0),
      0,
    );

    return { totalSeats, totalPrice };
  };

  const venue_place_id = isGroup
    ? orderDetails?.address?.google_place_id
    : data?.address?.google_place_id;

  const { totalSeats, totalPrice } = calculateTotals(orderDetails?.ticket);

  const minSeat = orderDetails?.ticket?.min_per_transaction ?? 1;
  const maxSeat = orderDetails?.ticket?.max_per_transaction ?? Infinity;
  const isSelectedTicketUnavailable = useMemo(() => {
    const selectedId = orderDetails?.ticket?.id;
    if (!selectedId) return false;
    if (!Array.isArray(tickets) || tickets.length === 0) return false;

    const selected = tickets.find((t) => String(t?.id) === String(selectedId));
    if (!selected) return false;

    const available = Number(
      selected?.available ?? selected?.available_quantity ?? 0,
    );
    const minPerTxn = Number(selected?.min_per_transaction ?? 1);
    const soldOutFlag =
      selected?.is_sold_out === true ||
      selected?.is_sold_out === 1 ||
      selected?.is_sold_out === "1";

    return (
      soldOutFlag ||
      !Number.isFinite(available) ||
      available <= 0 ||
      available < minPerTxn
    );
  }, [tickets, orderDetails?.ticket?.id]);

  //this is for  application
  const sendApplicationEvent = async () => {
    try {
      const isSeatSelection =
        activeStep === "seating" ||
        (activeStep === "zone" &&
          interactionMode === "zoom" &&
          selectedSeats?.length > 0);

      const selectionType = isSeatSelection ? "seat" : "zone";
      const session_id = searchParams.get("session_id");
      const seat_ids =
        selectedSeats?.length > 0 ? selectedSeats.map((s) => s.seat_id) : [];
      const res = await fetch(
        process.env.NEXT_PUBLIC_API_URL +
          "/" +
          ENDPOINTS.APPLICATION_WEBVIEW_HELP,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            event_id: data?.id || orderDetails?.ticket?.event_id,
            session_id: session_id,
            seats: seat_ids,
            selection_type: selectionType,
            ticket_id: orderDetails?.ticket?.id,
          }),
        },
      );

      const dataRes = await res.json();

      if (!res.ok) {
        throw new Error(dataRes?.message || "Something went wrong");
      }
    } catch (err) {
      console.error("API Error:", err.message);
    }
  };

  //this is for appliaction back
  const backApplicationEvent = async () => {
    const session_id = searchParams.get("session_id");
    try {
      const res = await fetch(
        process.env.NEXT_PUBLIC_API_URL +
          "/" +
          ENDPOINTS.APPLICATION_WEBVIEW_HELP,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            is_back_button: true,
            session_id: session_id,
          }),
        },
      );

      const dataRes = await res.json();

      if (!res.ok) {
        throw new Error(dataRes?.message || "Something went wrong");
      }
    } catch (err) {
      console.error("API Error:", err.message);
    }
  };
  useEffect(() => {
    // Only clear seat selection and cache when user goes back to venue/datetime (not tickets/review).
    if (activeStep === "venue" || activeStep === "datetime") {
      setGoToSeatSelection(false);
      setSelectedSeats([]);
      setOrderDetails((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          ticket: null,
          slot: null,
          zone: null,
          seating_layout_data: null,
          zone_seats: [],
          seating_layout_event_id: null,
          seating_layout_zone_id: null,
        };
      });
    }
  }, [activeStep, setOrderDetails]);

  // Restore seat selection when returning from review/tickets to seating.
  useEffect(() => {
    const prevStep = prevStepRef.current;
    prevStepRef.current = activeStep;

    const onSeatStep =
      activeStep === "seating" ||
      (activeStep === "zone" && interactionMode === "zoom");
    const wasOnSeatStep =
      prevStep === "seating" ||
      (prevStep === "zone" && interactionMode === "zoom");

    // Only restore when ENTERING the seat step from a different step.
    // Prevents restore from fighting user-initiated deselection / Clear All.
    if (
      onSeatStep &&
      !wasOnSeatStep &&
      selectedSeats.length === 0 &&
      orderDetails?.ticket?.seats?.length > 0
    ) {
      setSelectedSeats(orderDetails.ticket.seats);
    }
  }, [
    activeStep,
    interactionMode,
    orderDetails?.ticket?.seats,
    selectedSeats.length,
    setSelectedSeats,
  ]);

  const resolveZoneTicket = useCallback(() => {
    const zone = orderDetails?.zone;
    const zoneLabel =
      zone?.zone_lable || zone?.label || zone?.name || zone?.category;
    const zoneTicketId = zone?.ticket_id;
    const firstSeatTicketId = selectedSeats[0]?.ticket_id;
    const ticketId = zoneTicketId || firstSeatTicketId;
    const ticketConfig = tickets?.find(
      (t) => String(t.id) === String(ticketId),
    );

    return {
      ticketId,
      ticketConfig,
      zoneLabel,
      displayName: zoneLabel || ticketConfig?.name || tickets?.[0]?.name,
    };
  }, [orderDetails?.zone, selectedSeats, tickets]);

  // Sync selected seats to orderDetails in zoom mode
  useEffect(() => {
    if (
      activeStep === "zone" &&
      interactionMode === "zoom" &&
      selectedSeats.length > 0
    ) {
      const seatIds = selectedSeats
        .map((seat) => seat.seat_id || seat.uuid)
        .filter(Boolean);
      const totalPrice = selectedSeats.reduce(
        (sum, seat) => sum + (seat?.price || 0),
        0,
      );

      const { ticketId, ticketConfig, displayName } = resolveZoneTicket();
      const unitPrice =
        ticketConfig?.price ??
        (selectedSeats.length > 0
          ? totalPrice / selectedSeats.length
          : selectedSeats[0]?.price || 0);

      const orderPayload = {
        subtotal: totalPrice,
        total_amount: totalPrice,
        ticket: {
          id: ticketId,
          qty: selectedSeats.length,
          price: unitPrice,
          name: displayName,
          event_id:
            ticketConfig?.event_id ||
            orderDetails?.event_id ||
            tickets?.[0]?.event_id ||
            data?.id,
          currency: ticketConfig?.currency || data?.currency || "INR",
          seat_id: seatIds,
          seats: selectedSeats,
          requires_approval:
            ticketConfig?.requires_approval || tickets?.[0]?.requires_approval,
          min_per_transaction:
            ticketConfig?.min_per_transaction ??
            tickets?.[0]?.min_per_transaction,
          max_per_transaction:
            ticketConfig?.max_per_transaction ??
            tickets?.[0]?.max_per_transaction,
        },
        session_id: orderDetails?.session_id,
      };

      setOrderDetails((prev) => ({
        ...prev,
        ...orderPayload,
      }));
    } else if (
      activeStep === "zone" &&
      interactionMode === "zoom" &&
      selectedSeats.length === 0
    ) {
      setOrderDetails((prev) => ({
        ...prev,
        ticket: undefined,
      }));
    }
  }, [
    selectedSeats,
    activeStep,
    interactionMode,
    tickets,
    data?.currency,
    data?.id,
    orderDetails?.event_id,
    orderDetails?.session_id,
    resolveZoneTicket,
    setOrderDetails,
  ]);

  /* ---------------- MODAL ---------------- */

  const ConfirmationModal = (
    <Modal
      open={openConfirm}
      onCancel={() => {
        setOpenConfirm(false);
        setPendingStep(null);
      }}
      title="Confirmation"
      centered
      okText="Yes"
      cancelText="No"
      onOk={async (e) => {
        setOpenConfirm(false);

        if (activeStep === "tickets") {
          // No session exists in the ticket flow — just reset zone + ticket
          setOrderDetails((prev) => ({
            ...prev,
            zone: null,
            ticket: null,
          }));
        } else {
          const eventId =
            orderDetails?.ticket?.event_id ||
            orderDetails?.event_id ||
            data?.id;
          await deleteSession(orderDetails?.session_id, eventId);
          setSelectedSeats([]);
          setOrderDetails((prev) => ({
            ...prev,
            session_id: null,
            session_created_at: null,
            seating_layout_data: null,
            zone_seats: [],
            seating_layout_event_id: null,
            seating_layout_zone_id: null,
            // Keep the selected zone when going back to seating so the layout
            // re-fetches with the correct zone_id filter (e.g. Silver only).
            zone: pendingStep === "seating" ? prev.zone : null,
            ticket: prev.ticket
              ? { ...prev.ticket, seats: [], seat_id: [], qty: 0 }
              : prev.ticket,
          }));
        }

        setActiveStep(pendingStep);
        setPendingStep(null);
      }}
    >
      <p>If you go back now, your selection will be lost.</p>
      <p>Are you sure you want to go back?</p>
    </Modal>
  );
  const closeRequestModal = () => {
    setApprovalModal(false);
    router.push("/tickets");
  };

  const approvalModalComponent = (
    <Modal
      open={approvalModal}
      onCancel={closeRequestModal}
      footer={null}
      centered
      width={450}
      title={"Request Submitted"}
    >
      <div style={{ textAlign: "center", padding: "10px 0" }}>
        <Text
          type="secondary"
          style={{ fontSize: "15px", display: "block", marginBottom: "25px" }}
        >
          This Ticket requires manual approval from the event organizer before
          you proceed with the payment.
        </Text>

        <Button type="primary" size="large" onClick={closeRequestModal}>
          Close
        </Button>
      </div>
    </Modal>
  );

  /* ---------------- DYNAMIC STEPS (single source of truth) ---------------- */
  const flowConditions = useMemo(
    () => ({
      isGroup,
      needsVenueSelection: Boolean(needsVenueSelection),
      isMultiDate: deriveMultiDateCondition(dates),
      isZone: is_zone,
      isSitting: orderDetails?.zone
        ? !!orderDetails.zone.withSeating
        : !!is_sitting,
      haveConfiguredSeats: orderDetails?.zone
        ? !!orderDetails.zone.withSeating
        : !!haveConfiguredSeats,
      interactionMode: interactionMode,
      ticketStepActive:
        activeStep === "tickets" && !!orderDetails?.zone?.withSeating,
      seasonPassStepActive:
        activeStep === "season_passes" ||
        Boolean(orderDetails?.season_pass_step),
      bookingPath: orderDetails?.booking_path || null,
      slotShowId: slotShowId || null,
    }),
    [
      slotShowId,
      isGroup,
      needsVenueSelection,
      dates,
      is_zone,
      is_sitting,
      haveConfiguredSeats,
      orderDetails?.zone,
      orderDetails?.booking_path,
      orderDetails?.season_pass_step,
      activeStep,
      interactionMode,
    ],
  );

  const availableSteps = useMemo(
    () => getAvailableSteps(flowConditions),
    [flowConditions],
  );

  // Debug in development
  useEffect(() => {
    debugFlow(
      "availableSteps recomputed",
      flowConditions,
      activeStep,
      availableSteps,
    );
  }, [availableSteps]);
  /* ---------------- SESSION CLEANUP ---------------- */

  const handleSessionExpire = useCallback(async () => {
    const eventId =
      orderDetails?.ticket?.event_id || orderDetails?.event_id || data?.id;
    await deleteSession(orderDetails?.session_id, eventId);
    setSelectedSeats([]);
    setOrderDetails((prev) => ({
      ...prev,
      session_id: null,
      session_created_at: null,
      seating_layout_data: null,
      zone_seats: [],
      seating_layout_event_id: null,
      seating_layout_zone_id: null,
      ticket: prev.ticket
        ? { ...prev.ticket, seats: [], seat_id: [], qty: 0 }
        : prev.ticket,
    }));
    message.warning(
      "Your seat reservation has expired. Please re-select your seats.",
    );
    setActiveStep("seating");
  }, [
    orderDetails?.session_id,
    orderDetails?.ticket?.event_id,
    orderDetails?.event_id,
    data?.id,
  ]);

  /* ---------------- TABS ---------------- */

  if (render === "tab")
    return (
      <>
        {ConfirmationModal}
        <Tabs
          activeKey={activeStep}
          onChange={(key) => {
            const stepKeys = availableSteps.map((s) => s.key);
            const nextIdx = stepKeys.indexOf(key);
            const currentIdx = stepKeys.indexOf(activeStep);

            if (nextIdx > currentIdx) return;

            if (activeStep === "review" && key === "seating") {
              setOpenConfirm(true);
              setPendingStep(key);
              return;
            }

            if (activeStep === "tickets" && key === "zone") {
              if (orderDetails?.ticket?.qty > 0) {
                setPendingStep(key);

                setOpenConfirm(true);
                return;
              } else {
                setActiveStep(key);
                setOrderDetails((prev) => ({
                  ...prev,
                  zone: null,
                  ticket: null,
                }));
                return;
              }
            }
            if (
              (activeStep === "seating" ||
                (activeStep === "zone" && interactionMode === "zoom")) &&
              selectedSeats.length > 0
            ) {
              setOpenConfirm(true);
              setPendingStep(key);
              return;
            }

            setActiveStep(key);
          }}
          items={availableSteps.map((step, index) => ({
            key: step.key,
            label: step.label,
            icon: getStepIcon(step.key, index),
          }))}
        />
      </>
    );

  /* ---------------- BACK HANDLER ---------------- */

  const handleBack = (e) => {
    e?.stopPropagation?.();

    if (is_application === "true") {
      // if (activeStep === "seating") {
      backApplicationEvent();
      return;
      // }

      // if (activeStep === "zone" && interactionMode === "zoom") {
      //   backApplicationEvent();
      //   return;
      // }
    }
    if (activeStep === "tickets" && orderDetails?.zone?.withSeating) {
      setOrderDetails((prev) => ({
        ...prev,
        zone: null,
        ticket: null,
      }));
      setActiveStep("seating");
      return;
    }

    if (activeStep === "season_passes") {
      const entry = orderDetails?.season_pass_entry;
      const backTo =
        entry === "datetime_screen" || entry === "datetime"
          ? "datetime"
          : entry === "venue_screen" || entry === "venue"
            ? "venue"
            : orderDetails?.date
              ? "datetime"
              : availableSteps.some((s) => s.key === "venue")
                ? "venue"
                : "datetime";
      setOrderDetails((prev) => ({
        ...prev,
        ticket: null,
        season_pass_step: false,
        booking_path: null,
        is_season_pass: false,
        season_pass_entry: null,
      }));
      setActiveStep(backTo);
      return;
    }

    if (
      activeStep === "review" &&
      orderDetails?.booking_path === "season_pass"
    ) {
      setOrderDetails((prev) => ({
        ...prev,
        season_pass_step: true,
      }));
      setActiveStep("season_passes");
      return;
    }

    // Came to Date & Slot from the "Open daily / pick a show date" choice:
    // go back to that choice (the date step hides while a season is picked)
    if (activeStep === "slot" && orderDetails?._venue_dates) {
      setOrderDetails((prev) => ({
        ...prev,
        event_id: null,
        slot: null,
        pending_date: null,
        date: null,
        time: null,
        end_date: null,
        end_time: null,
        ticket: null,
      }));
      setActiveStep("datetime");
      return;
    }

    const prev = getPrevStep(activeStep, availableSteps);

    if (!prev) {
      // Booking pages live at .../book — go back to the event page one
      // segment up, whatever the current city/category prefix is.
      router.push(pathname.replace(/\/book\/?$/, ""));
      return;
    }

    if (activeStep === "seating" && prev === "zone") {
      if (orderDetails?.ticket?.qty > 0) {
        setPendingStep(prev);
        setOpenConfirm(true);
        return;
      } else {
        setActiveStep(prev);
        setOrderDetails((prev) => ({
          ...prev,
          zone: null,
          ticket: null,
        }));
        return;
      }
    }
    if (
      activeStep === "seating" ||
      (activeStep === "zone" && interactionMode === "zoom")
    ) {
      setSelectedSeats([]);
      setGoToSeatSelection(false);
    }

    if (activeStep === "review" && prev === "zone") {
      if (orderDetails?.ticket?.seats?.length) {
        setSelectedSeats(orderDetails.ticket.seats);
      }
      setActiveStep("zone");
      return;
    }
    if (activeStep === "review" && (prev === "seating" || prev === "zone")) {
      setPendingStep(prev);
      setOpenConfirm(true);
      return;
    }

    if (activeStep === "tickets" && prev === "zone") {
      if (orderDetails?.ticket?.qty > 0) {
        setPendingStep(prev);

        setOpenConfirm(true);
        return;
      } else {
        setActiveStep(prev);
        setOrderDetails((prev) => ({
          ...prev,
          zone: null,
          ticket: null,
        }));
        return;
      }
    }
    if (activeStep === "tickets" && prev === "seating") {
      if (orderDetails?.ticket?.seats?.length) {
        setSelectedSeats(orderDetails.ticket.seats);
      }
      setActiveStep("seating");
      return;
    }
    setActiveStep(prev);
  };

  /* ---------------- NEXT HANDLER ---------------- */

  const handleNext = async () => {
    if (is_application === "true") {
      if (activeStep === "seating" && selectedSeats?.length > 0) {
        sendApplicationEvent();
        return;
      }

      if (
        activeStep === "zone" &&
        interactionMode === "zoom" &&
        selectedSeats?.length > 0
      ) {
        sendApplicationEvent();
        return;
      }
    }
    // Validation guards
    if (activeStep === "venue" && !orderDetails?.venue) return;
    if (activeStep === "slot" && !orderDetails?.slot) return;
    if (
      activeStep === "datetime" &&
      (!orderDetails?.date || !orderDetails?.time)
    )
      return;
    if (activeStep === "season_passes") {
      if (!orderDetails?.ticket?.is_season_pass) return;
      setOrderDetails((prev) => ({
        ...prev,
        booking_path: "season_pass",
        season_pass_step: false,
      }));
      setActiveStep("review");
      return;
    }
    if (activeStep === "datetime") {
      // Footer Next = individual ticket path (season pass is optional via CTA).
      setOrderDetails((prev) => ({
        ...prev,
        booking_path: "individual",
        season_pass_step: false,
        is_season_pass: false,
      }));
      const individualFlow = getAvailableSteps({
        ...flowConditions,
        bookingPath: "individual",
        seasonPassStepActive: false,
      });
      const nextIndividual = getNextStep("datetime", individualFlow);
      if (nextIndividual) setActiveStep(nextIndividual);
      return;
    }
    if (activeStep === "zone") {
      if (!orderDetails?.zone) return;
    }
    if (activeStep === "seating" && selectedSeats.length === 0) return;

    // Create session just before going to review so seats get locked server-side.
    // Pass ticket_id for seat-selection mode (non-zone) so the backend locks
    // the correct ticket's seats.
    if (activeStep === "seating" && selectedSeats.length > 0) {
      try {
        const eventId =
          orderDetails?.ticket?.event_id || orderDetails?.event_id || data?.id;
        // Derive ticket_id: prefer the resolved ticket on orderDetails, then
        // fall back to the ticket_id carried on the first selected seat.
        const ticketId =
          orderDetails?.ticket?.id ||
          selectedSeats[0]?.ticket_id ||
          null;
        const seatIds = selectedSeats.map((s) => s.seat_id);
        const sessionId = await createSeatSession(eventId, { 
          ticketId, 
          seats: seatIds, 
          selectionType: "seat" 
        });
        setOrderDetails((prev) => ({
          ...prev,
          session_id: sessionId,
          session_created_at: Date.now(),
        }));
      } catch (err) {
        message.error(
          err?.message || "Failed to create session. Please try again.",
        );
        return;
      }
    }
    if (activeStep === "tickets" && !orderDetails?.ticket) return;
    // Season pass chosen on Tickets step → skip zone/seating, go to Review.
    if (activeStep === "tickets" && orderDetails?.ticket?.is_season_pass) {
      setOrderDetails((prev) => ({
        ...prev,
        booking_path: "season_pass",
        is_season_pass: true,
        season_pass_step: false,
      }));
      setActiveStep("review");
      return;
    }
    if (activeStep === "tickets" && orderDetails?.zone?.withSeating) {
      setActiveStep("review");
      return;
    }

    if (activeStep === "review" && orderDetails?.ticket) {
      if (isSelectedTicketUnavailable) {
        message.warning("Selected ticket is sold out. Please choose another ticket.");
        setOrderDetails((prev) =>
          prev ? { ...prev, ticket: null, subtotal: 0, total_amount: 0 } : prev,
        );
        setActiveStep("tickets");
        return;
      }
      if (alreadyRegistered && data?.is_unique_registration) {
        message.error(
          "You already have a registration for this event. You cannot book again with this account.",
        );
        return;
      }
      // Same decision either way: skip the form only for a single, non-custom
      // registration. Logging in mid-booking must not shortcut this — it
      // used to jump straight to payment after login, even when the ticket
      // needed multiple registrations or custom fields.
      const proceedToRegistration = (loggedInUser) => {
        if (
          !data.is_custom_form &&
          (data.show_single_registration || orderDetails.ticket?.qty === 1)
        ) {
          createOrder([
            {
              first_name: loggedInUser.first_name,
              last_name: loggedInUser.last_name,
              email: loggedInUser.email,
              phone: loggedInUser.phone,
            },
          ]);
        } else {
          setCustomModal(true);
        }
      };

      if (user?.id) {
        proceedToRegistration(user);
      } else {
        setIsOpen(true);
        setSpecialFn({ fn: (up) => proceedToRegistration(up?.[0]) });
      }
      return;
    }

    const next = getNextStep(activeStep, availableSteps);
    if (next) setActiveStep(next);
  };

  /* ---------------- BUTTONS ---------------- */

  if (render === "buttons")
    return (
      <>
        {ConfirmationModal}
        <Flex justify="space-between" align="center" className="w-full">
          <div className="flex gap-3 items-center">
            <Button
              onClick={(e) => {
                handleBack(e);
              }}
              disabled={loading || parentLoading}
            >
              <IconArrowNarrowLeft size={18} />
              <span className="hidden sm:block">Back</span>
            </Button>

            {(activeStep === "seating" ||
              (activeStep === "zone" && interactionMode === "zoom")) && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setSelectedSeats([]);
                }}
                className="w-full"
                disabled={selectedSeats.length === 0}
              >
                Clear All
              </Button>
            )}
          </div>
          <div
            className={`flex gap-3 items-center justify-center ${
              totalSeats > 0 &&
              (activeStep === "seating" ||
                (activeStep === "zone" && interactionMode === "zoom"))
                ? "w-full"
                : ""
            }`}
          >
            {(activeStep === "seating" ||
              (activeStep === "zone" && interactionMode === "zoom")) &&
              totalSeats > 0 && (
                <div className="flex justify-center w-full items-center">
                  <p className="text-center flex gap-2 sm:gap-4 items-center">
                    {/* Hide "Total" label on tiny screens */}
                    <span className="text-xl sm:text-2xl font-extralight text-[#021526] hidden sm:inline">
                      Total{" "}
                    </span>
                    <span className="text-xl sm:text-2xl font-extrabold text-[#021526]">
                      {totalPrice}/-
                    </span>
                    {/* Simplified seat count for mobile */}
                    <span className="text-sm sm:text-lg font-bold text-[#A6E5C1]">
                      {totalSeats}{" "}
                      <span className="hidden sm:inline">
                        {totalSeats > 1 ? "seats" : "seat"}
                      </span>
                    </span>
                  </p>
                </div>
              )}
            <Button
              type="primary"
              loading={loading || parentLoading}
              onClick={handleNext}
              className=""
              disabled={
                activeStep === "venue"
                  ? !orderDetails?.venue
                  : activeStep === "datetime"
                    ? !orderDetails?.date || !orderDetails?.time
                    : activeStep === "slot"
                      ? !orderDetails?.slot
                    : activeStep === "season_passes"
                      ? !orderDetails?.ticket?.is_season_pass
                      : activeStep === "zone"
                      ? !orderDetails?.zone ||
                        (interactionMode === "zoom" &&
                          orderDetails.zone.withSeating &&
                          selectedSeats.length === 0)
                      : activeStep === "seating"
                        ? selectedSeats.length < minSeat ||
                          selectedSeats.length > maxSeat
                        : activeStep === "tickets"
                          ? !orderDetails?.ticket
                          : activeStep === "review"
                            ? !orderDetails?.ticket ||
                              isSelectedTicketUnavailable ||
                              (alreadyRegistered &&
                                data?.is_unique_registration)
                            : false
              }
            >
              {activeStep === "review" ? (
                <Flex align="center" gap={5}>
                  {is_required
                    ? "Request now"
                    : parseFloat(orderDetails?.total_amount) === 0
                      ? "Book"
                      : "Pay now"}
                  <IconCheck size={18} style={{ marginTop: "2px" }} />
                </Flex>
              ) : activeStep === "zone" ? (
                "Next"
              ) : (
                <Flex align="center" gap={5}>
                  <IconArrowNarrowRight
                    size={18}
                    style={{ marginTop: "2px" }}
                  />
                  <span className="hidden sm:block">Next</span>
                </Flex>
              )}
            </Button>
          </div>
        </Flex>
      </>
    );

  /* ---------------- STEP RENDER ---------------- */

  const stepComponents = {
    venue: (
      <VenueStep
        data={data}
        setTickets={setTickets}
        setActiveStep={setActiveStep}
        setDates={setDates}
        setOrderDetails={setOrderDetails}
        orderDetails={orderDetails}
        availableSteps={availableSteps}
      />
    ),

    datetime: (
      <DateTimeStep
        dates={dates}
        data={data}
        setActiveStep={setActiveStep}
        setTickets={setTickets}
        setOrderDetails={setOrderDetails}
        orderDetails={orderDetails}
        availableSteps={availableSteps}
        needsVenueSelection={Boolean(needsVenueSelection)}
      />
    ),

    slot: (
      <SlotStep
        showId={slotShowId}
        baseTickets={tickets}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        setTickets={setTickets}
        setActiveStep={setActiveStep}
        availableSteps={availableSteps}
        showStartDate={
          data?.start_date ||
          orderDetails?.start_date ||
          orderDetails?.event?.start_date ||
          null
        }
      />
    ),

    season_passes: (
      <SeasonPassStep
        data={data}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        setActiveStep={setActiveStep}
        setTickets={setTickets}
        availableSteps={availableSteps}
      />
    ),

    zone: (
      <ZoneLayout
        data={data}
        isGroup={isGroup}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        activeStep={activeStep}
        is_sitting={is_sitting}
        haveConfiguredSeats={haveConfiguredSeats}
        setActiveStep={setActiveStep}
        availableSteps={availableSteps}
        selectedSeats={selectedSeats}
        setSelectedSeats={setSelectedSeats}
        tickets={tickets}
        onNoZones={onNoZones}
      />
    ),

    seating: (
      <SeatingLayout
        data={data}
        tickets={tickets}
        isGroup={isGroup}
        selectedSeats={selectedSeats}
        setSelectedSeats={setSelectedSeats}
        goToSeatselection={goToSeatselection}
        setGoToSeatSelection={setGoToSeatSelection}
        setOrderDetails={setOrderDetails}
        orderDetails={orderDetails}
        venue_place_id={venue_place_id}
        haveConfiguredSeats={haveConfiguredSeats}
        setActiveStep={setActiveStep}
        activeStep={activeStep}
        availableSteps={availableSteps}
        showAvailableTickets={Boolean(data?.show_available_tickets)}
        isUniqueRegistration={Boolean(data?.is_unique_registration)}
        isTicketTransferable={isTicketTransferable}
      />
    ),

    tickets: (
      <TicketStep
        data={data}
        tickets={tickets}
        setTickets={setTickets}
        setOrderDetails={setOrderDetails}
        orderDetails={orderDetails}
        setActiveStep={setActiveStep}
        availableSteps={availableSteps}
        showAvailableTickets={Boolean(data?.show_available_tickets)}
        alreadyRegistered={alreadyRegistered}
        isUniqueRegistration={Boolean(data?.is_unique_registration)}
        isTicketTransferable={isTicketTransferable}
        order_id={enrollmentOrderId}
      />
    ),
  };

  return (
    stepComponents[activeStep] ?? (
      <>
        <UserWrapper>
          <Review
            data={data}
            orderDetails={orderDetails}
            setOrderDetails={setOrderDetails}
            setParentLoading={setParentLoading}
            onSessionExpire={handleSessionExpire}
          />
        </UserWrapper>
        {approvalModalComponent}
      </>
    )
  );
};

export default ComponentToRender;
